package com.ijoin.deeplink

/**
 * Represents a parsed deep link
 */
sealed class DeepLink {
    data class Event(val eventId: String) : DeepLink()
    data class Ticket(val ticketId: String) : DeepLink()
    data class User(val userId: String) : DeepLink()
    data class PasswordReset(val token: String) : DeepLink()
    data class GoogleAuthExchange(val code: String) : DeepLink()
    data object Home : DeepLink()
}

/**
 * Deep link parser - parses URLs into DeepLink objects
 */
object DeepLinkParser {
    // Base URL for the app - should be configured per environment
    const val SCHEME = "ijoin"
    const val WEB_HOST = "ijoin.info" // Change this to your actual domain

    /**
     * Parses a URL string into a DeepLink
     * Supports:
     * - ijoin://event/{eventId}
     * - https://ijoin.info/event/{eventId}
     * - /event/{eventId} (path only)
     */
    fun parse(url: String): DeepLink? {
        val cleanUrl = url.trim()

        // Extract path from different URL formats
        val pathWithQuery = when {
            // Custom scheme: ijoin://event/123
            cleanUrl.startsWith("$SCHEME://") -> {
                cleanUrl.removePrefix("$SCHEME://")
            }
            // Web URL: https://ijoin.info/event/123
            cleanUrl.contains("://") -> {
                val afterScheme = cleanUrl.substringAfter("://")
                afterScheme.substringAfter("/", "")
            }
            // Path only: /event/123 or event/123
            cleanUrl.startsWith("/") -> {
                cleanUrl.removePrefix("/")
            }
            else -> cleanUrl
        }

        val pathOnly = pathWithQuery.substringBefore("?").substringBefore("#")
        val queryPart = pathWithQuery.substringAfter("?", "")
        val segments = pathOnly.split("/").filter { it.isNotEmpty() }
        val tokenFromQuery = queryPart
            .substringBefore("#")
            .split("&")
            .mapNotNull { param ->
                if (param.startsWith("token=")) {
                    param.substringAfter("token=").takeIf { it.isNotBlank() }
                } else {
                    null
                }
            }
            .firstOrNull()
        val exchangeCode = queryPart
            .substringBefore("#")
            .split("&")
            .mapNotNull { param ->
                if (param.startsWith("exchange_code=")) {
                    param.substringAfter("exchange_code=").takeIf { it.isNotBlank() }
                } else {
                    null
                }
            }
            .firstOrNull()

        return when {
            !exchangeCode.isNullOrBlank() && (segments.isEmpty() || segments[0] == "oauth") -> {
                DeepLink.GoogleAuthExchange(exchangeCode)
            }
            segments.isEmpty() -> DeepLink.Home
            segments[0] == "event" && segments.size >= 2 -> {
                DeepLink.Event(segments[1])
            }
            segments[0] == "ticket" && segments.size >= 2 -> {
                DeepLink.Ticket(segments[1])
            }
            segments[0] == "user" && segments.size >= 2 -> {
                DeepLink.User(segments[1])
            }
            segments[0] == "reset-password" -> {
                val token = when {
                    segments.size >= 3 && segments[1] == "token" -> segments[2]
                    segments.size >= 2 -> segments[1]
                    !tokenFromQuery.isNullOrBlank() -> tokenFromQuery
                    else -> null
                }
                token?.let { DeepLink.PasswordReset(it) }
            }
            else -> null
        }
    }
}

/**
 * Deep link URL builder
 */
object DeepLinkBuilder {
    /**
     * Builds a web URL for an event
     */
    fun eventUrl(eventId: String, baseUrl: String? = null): String {
        val host = baseUrl ?: "https://${DeepLinkParser.WEB_HOST}"
        return "$host/event/$eventId"
    }

    /**
     * Builds a web URL for a ticket
     */
    fun ticketUrl(ticketId: String, baseUrl: String? = null): String {
        val host = baseUrl ?: "https://${DeepLinkParser.WEB_HOST}"
        return "$host/ticket/$ticketId"
    }

    /**
     * Builds a web URL for a user profile
     */
    fun userUrl(userId: String, baseUrl: String? = null): String {
        val host = baseUrl ?: "https://${DeepLinkParser.WEB_HOST}"
        return "$host/user/$userId"
    }
}
